import {
    faClockRotateLeft,
    faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';
import axios from 'axios';
import { parse } from 'qs';
import type TripPriceLoader from './TripPriceLoader';
import ItinerarySearchForm from '@search/ItinerarySearchForm';
import { TripType, type TripUrlParams, type TripPathData } from '@utils/trip';
import {
    type Location,
    PaxType,
    SearchMode,
    type DateSpanObject,
} from '@utils/types';
import {
    type UrlStringParams,
    type LocationGroup,
    SearchFromErrorMessage,
    type dayChangerFunc,
} from '@search/types';
import { VerifyPathError } from '@utils/itinerary';
import type { AnalyticsAttribute } from '@store/analytics';
import type TripPackageSet from '@itinerary/trip/TripPackageSet';

const RECENT_ITEMS_LIMIT = 3;

export abstract class TripSearchForm<
    TLocation extends Location<TLocation> = Location,
    TPathData extends TripPathData = TripPathData,
> extends ItinerarySearchForm<TLocation, TPathData> {
    // final
    override get destination(): TLocation | null {
        return this.isDomestic
            ? this.domesticDestination
            : this.foreignDestination;
    }

    // final
    get origin(): TLocation | null {
        return this.isDomestic ? this.domesticOrigin : this.foreignOrigin;
    }

    // final
    override get searchReady() {
        return !!(
            this.originCode &&
            this.destinationCode &&
            this.departureDate
        ); // && this.adultCount && this.childCount && this.infantCount;
    }

    // final
    get origins() {
        return this.isDomestic ? this.domesticOrigins : this.foreignOrigins;
    }

    get originsWithKeywords() {
        return this.origins.map(l => ({
            ...l,
            keywords: this.getLocationKeywords(l),
        }));
    }

    // final
    get originCode() {
        return this.origin?.code || null;
    }

    // final
    get destinationCode() {
        return this.destination?.code || null;
    }

    // final
    get formattedDepartureDate() {
        if (!this.departureDate) {
            return '';
        }
        return dayConfig.jalali(this.departureDate).format(formatter());
    }

    // final
    get formattedReturnDate() {
        if (!this.returnDate) {
            return '';
        }
        return dayConfig.jalali(this.returnDate).format(formatter());
    }

    // final
    domesticOrigin: TLocation | null = null;
    // final
    domesticDestination: TLocation | null = null;
    // final
    foreignOrigin: TLocation | null = null;
    // final
    foreignDestination: TLocation | null = null;
    // final
    destinationExtraLocations: TLocation[] = [];
    // final
    originExtraLocations: TLocation[] = [];
    // final
    domesticRecentLocations: TLocation[] = [];
    // final
    foreignRecentLocations: TLocation[] = [];
    // final
    tripType: TripType = TripType.OneWay;
    // final
    public override departureDate: Date | null = null;
    // final
    returnDate: Date | null = null;
    // final
    originSearchKeyword: {
        [SearchMode.Domestic]: string;
        [SearchMode.Foreign]: string;
    } = {
        [SearchMode.Domestic]: '',
        [SearchMode.Foreign]: '',
    };

    // final
    destinationSearchKeyword: {
        [SearchMode.Domestic]: string;
        [SearchMode.Foreign]: string;
    } = {
        [SearchMode.Domestic]: '',
        [SearchMode.Foreign]: '',
    };

    abstract popularForeignOrigins: TLocation[];
    abstract popularForeignDestinations: TLocation[];
    abstract popularDomesticOrigins: TLocation[];
    abstract popularDomesticDestinations: TLocation[];
    abstract get foreignOrigins(): TLocation[];
    abstract get foreignDestinations(): TLocation[];
    abstract get domesticOrigins(): TLocation[];
    abstract get domesticDestinations(): TLocation[];

    override verifyViewPath(path: string, pathData: TPathData): Promise<void> {
        const today = dayConfig.gregory().toDate();
        let departureDate = pathData.departureDate
            ? parseDate(pathData.departureDate)!
            : today;
        const returnDate = pathData.returnDate
            ? parseDate(pathData.returnDate)!
            : undefined;
        const newQueries: { departureDate?: string; returnDate?: string } = {};
        if (
            pathData.departureDate &&
            dayConfig.gregory(departureDate).isBefore(today, 'day')
        ) {
            newQueries.departureDate = dayConfig
                .jalali(today)
                .format(formatter());
            departureDate = today;
        }
        if (pathData.returnDate) {
            const dayAfterDeparture = dayConfig
                .gregory(departureDate)
                .add(1, 'days');
            if (
                dayConfig.gregory(returnDate).isBefore(dayAfterDeparture, 'day')
            ) {
                newQueries.returnDate = dayConfig
                    .jalali(dayAfterDeparture)
                    .format(formatter());
            }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { origin, destination, ...query } = pathData;
        if (Object.keys(newQueries).length) {
            return Promise.reject(
                new VerifyPathError(
                    'queries not valid',
                    301,
                    true,
                    path,
                    parse({ ...query, ...newQueries } as any) as {
                        [key: string]: string;
                    },
                    true
                )
            );
        } else {
            return Promise.resolve();
        }
    }

    // final
    override priceLoader: TripPriceLoader | undefined = undefined;

    // ship only
    get recentLocations(): TLocation[] {
        const recentList = this.isDomestic
            ? this.domesticRecentLocations
            : this.foreignRecentLocations;
        return recentList.map(location => ({
            ...location,
            subLocations: [],
            icon: faClockRotateLeft,
        }));
    }

    // final
    get popularOrigins(): TLocation[] {
        return this.filterPopularLocations(
            this.isDomestic
                ? this.popularDomesticOrigins
                : this.popularForeignOrigins
        );
    }

    private filterPopularLocations(locations: TLocation[]) {
        return (locations || []).filter(
            (l: TLocation) =>
                !(this.recentLocations as TLocation[]).some(
                    (r: TLocation) => r.code === l.code
                )
        );
    }

    // final
    get destinations() {
        return this.isDomestic
            ? this.domesticDestinations
            : this.foreignDestinations;
    }

    get destinationsWithKeywords() {
        return this.destinations.map(l => ({
            ...l,
            keywords: this.getLocationKeywords(l),
        }));
    }

    // flight only
    get filterSearchLocations(): boolean {
        return true;
    }

    // final
    get filteredOrigins(): LocationGroup<TLocation>[] {
        if (!this.originSearchKeyword[this.searchMode]) {
            const list: LocationGroup<TLocation>[] = [];
            if (this.recentLocations.length) {
                list.push({
                    title: 'جستجوهای اخیر',
                    locations: this.recentLocations,
                    icon: faClockRotateLeft,
                    ignoreGroupInSearch: true,
                });
            }
            if (this.popularOrigins.length) {
                list.push({
                    title: 'شهرهای پرتردد',
                    locations: this.popularOrigins,
                    icon: faLocationDot,
                    ignoreGroupInSearch: true,
                });
            }
            return list;
        } else if (this.filterSearchLocations) {
            return [
                {
                    title: '',
                    locations: [
                        ...this.filterLocationsBaseOnToken(
                            this.originSearchKeyword[this.searchMode],
                            this.originsWithKeywords
                        ),
                        ...this.originExtraLocations,
                    ],
                },
            ];
        } else {
            return [
                {
                    title: '',
                    locations: [...this.origins, ...this.originExtraLocations],
                },
            ];
        }
    }

    // bus-only
    get showRecentLocationsInDestinations(): boolean {
        return true;
    }

    // final
    get popularDestinations(): TLocation[] {
        const all = this.isDomestic
            ? this.popularDomesticDestinations
            : this.popularForeignDestinations;
        if (this.showRecentLocationsInDestinations) {
            return this.filterPopularLocations(all);
        } else {
            return all;
        }
    }

    // final
    override get filteredDestinations(): LocationGroup<TLocation>[] {
        if (!this.destinationSearchKeyword[this.searchMode]) {
            const list: LocationGroup<TLocation>[] = [];
            if (
                this.showRecentLocationsInDestinations &&
                this.recentLocations.length
            ) {
                list.push({
                    title: 'جستجوهای اخیر',
                    locations: this.recentLocations,
                    icon: faClockRotateLeft,
                    ignoreGroupInSearch: true,
                });
            }
            if (this.popularDestinations.length) {
                list.push({
                    title: 'شهرهای پرتردد',
                    locations: this.popularDestinations,
                    icon: faLocationDot,
                    ignoreGroupInSearch: true,
                });
            }
            return list;
        } else if (this.filterSearchLocations) {
            return [
                {
                    title: '',
                    locations: [
                        ...this.filterLocationsBaseOnToken(
                            this.destinationSearchKeyword[this.searchMode],
                            this.destinationsWithKeywords
                        ),
                        ...this.destinationExtraLocations,
                    ],
                },
            ];
        } else {
            return [
                {
                    title: '',
                    locations: [
                        ...this.destinations,
                        ...this.destinationExtraLocations,
                    ],
                },
            ];
        }
    }

    abstract override generateViewUrl(
        params: UrlStringParams<TripUrlParams>
    ): string;

    // final
    override verifySearchForm(currentUrl: string): Promise<void> {
        let error;
        if (!this.origin || !this.destination) {
            error = 'مبدا و مقصد نمی‌تواند خالی باشد.';
        } else if (this.origin.code === this.destination.code) {
            error = 'مبدا و مقصد می بایست متفاوت انتخاب شوند.';
        } else if (!this.departureDate) {
            error = 'تاریخ رفت را انتخاب کنید.';
        } else if (this.tripType === TripType.RoundTrip) {
            if (!this.returnDate) {
                // error = 'تاریخ برگشت را انتخاب کنید.';
                this.setTripTypeAndCheckDate(TripType.OneWay);
            } else if (
                dayConfig
                    .gregory(this.departureDate)
                    .isAfter(this.returnDate, 'day')
            ) {
                error = 'تاریخ رفت نمی‌تواند قبل از تاریخ بازگشت باشد.';
            }
        }
        if (error) {
            return Promise.reject(error);
        }
        if (currentUrl && this.duplicateViewPath(currentUrl)) {
            return Promise.reject(
                new Error(SearchFromErrorMessage.DuplicatePath)
            );
        }
        return Promise.resolve();
    }

    // final
    setTripTypeAndCheckDate(type: TripType) {
        this.tripType = type;
        const { departureDate, returnDate } = this;
        if (
            type === TripType.RoundTrip &&
            departureDate &&
            (!returnDate ||
                dayConfig.gregory(returnDate).isBefore(departureDate, 'day'))
        ) {
            this.returnDate = dayConfig
                .gregory(departureDate)
                .add(1, 'day')
                .toDate();
        }
    }

    resetForeign() {
        this.foreignDestination = null;
        this.foreignOrigin = null;
    }

    switchDestinationToDomestic() {
        this.domesticDestination =
            [
                ...this.popularDomesticDestinations,
                ...this.domesticDestinations,
            ].find(({ title }) => this.foreignDestination?.title === title) ||
            null;
    }

    switchOriginToDomestic() {
        this.domesticOrigin =
            [...this.domesticOrigins, ...this.popularDomesticOrigins].find(
                ({ title }) => this.foreignOrigin?.title === title
            ) || null;
    }

    isLocationDomestic(location: Location | null) {
        return location?.countryName === 'ایران';
    }

    submitGuard() {
        if (
            this.searchMode === SearchMode.Foreign &&
            this.isLocationDomestic(this.destination) &&
            this.isLocationDomestic(this.origin)
        ) {
            this.searchMode = SearchMode.Domestic;
            this.switchDestinationToDomestic();
            this.switchOriginToDomestic();
            this.resetForeign();
        }
    }

    abstract getPriceLoader(
        origin: TLocation,
        destination: TLocation,
        tripType: TripType
    ): TripPriceLoader;

    override nextDay(callbackFunc: dayChangerFunc): void {
        const fromDateTomorrow = dayConfig
            .gregory(this.departureDate)
            .add(1, 'day')
            .startOf('day')
            .toDate();
        const toDateTomorrow = dayConfig
            .gregory(this.returnDate)
            .add(1, 'day')
            .startOf('day')
            .toDate();
        this.departureDate = fromDateTomorrow;
        this.returnDate = toDateTomorrow;
        const [url, query] = this.viewUrl.split('?');
        const queries = parse(query) as any;

        callbackFunc({
            path: url,
            query: queries,
        });
    }

    override prevDay(callbackFunc: dayChangerFunc): void {
        const fromDateYesterday = dayConfig
            .gregory(this.departureDate)
            .subtract(1, 'day')
            .startOf('day')
            .toDate();
        const toDateYesterday = dayConfig
            .gregory(this.returnDate)
            .subtract(1, 'day')
            .startOf('day')
            .toDate();
        this.departureDate = fromDateYesterday;
        this.returnDate = toDateYesterday;
        const [url, query] = this.viewUrl.split('?');
        const queries = parse(query) as any;

        callbackFunc({
            path: url,
            query: queries,
        });
    }

    // final
    override get loadPriceEnabled(): boolean {
        return true;
    }

    // final
    setOriginBaseOnCode(originCode: string) {
        if (!originCode) {
            return;
        }
        this.setOrigin(this.findLocationInLocations(originCode, this.origins));
    }

    // bus-only
    setDestinationBaseOnCode(destinationCode: string) {
        if (!destinationCode) {
            return;
        }
        this.setDestination(
            this.findLocationInLocations(destinationCode, this.destinations)
        );
    }

    // final
    setOrigin(origin: TLocation | null) {
        switch (this.searchMode) {
            case SearchMode.Domestic:
                this.domesticOrigin = origin;
                break;
            case SearchMode.Foreign:
                this.foreignOrigin = origin;
                break;
        }
    }

    override loadViewUrlParams(params: TripUrlParams) {
        if (params.departureDate) {
            this.departureDate = params.departureDate;
        }
        if (params.returnDate) {
            this.returnDate = params.returnDate!;
        }

        let searchMode: SearchMode = SearchMode.Domestic;
        const origin =
            this.findLocationInLocations(params.origin, this.domesticOrigins) ||
            this.findLocationInLocations(
                params.origin,
                this.domesticDestinations
            );
        if (!origin && this.domesticOrigins.length) {
            searchMode = SearchMode.Foreign;
        }

        const destination =
            this.findLocationInLocations(
                params.destination,
                this.domesticOrigins
            ) ||
            this.findLocationInLocations(
                params.destination,
                this.domesticDestinations
            );
        if (!destination && this.domesticDestinations.length) {
            searchMode = SearchMode.Foreign;
        }
        this.searchMode = searchMode;

        this.setOriginBaseOnCode(params.origin);
        this.setDestinationBaseOnCode(params.destination);

        if (params.returnDate) {
            this.setTripTypeAndCheckDate(TripType.RoundTrip);
        }

        this.setPaxTypeCount({
            type: PaxType.Adult,
            count: params.adultCount || 1,
        });
        this.setPaxTypeCount({
            type: PaxType.Child,
            count: params.childCount || 0,
        });
        this.setPaxTypeCount({
            type: PaxType.Infant,
            count: params.infantCount || 0,
        });
        return Promise.resolve();
    }

    override get searchAttributes(): AnalyticsAttribute {
        const packageSet = useCurrentItineraryModule().value
            ?.currentPackageSet as TripPackageSet | undefined;

        return {
            reserveType: `${this.reserveInfo.type}${
                this.isDomestic ? '' : '-i'
            }`,
            toname_simplified: packageSet?.destinationLocation?.title || '-',
            reserveTypeFa: this.reserveInfo.title,
            tripType:
                this.tripType === TripType.OneWay ? 'oneWay' : 'roundTrip',
            tripTypeFa:
                this.tripType === TripType.OneWay ? 'یک طرفه' : 'رفت و برگشت',
            from: this.origin?.code.toString(),
            fromName: this.origin?.title,
            to: this.destination?.code.toString(),
            toName: this.destination?.title,
            departureDate: this.departureDate
                ? dayConfig.gregory(this.departureDate).toDate()
                : undefined,
            returnDate:
                this.returnDate && this.tripType === TripType.RoundTrip
                    ? dayConfig.gregory(this.returnDate).toDate()
                    : undefined,
            departureDateString: this.departureDate
                ? dayConfig.gregory(this.departureDate).toISOString()
                : undefined,
            returnDateString:
                this.returnDate && this.tripType === TripType.RoundTrip
                    ? dayConfig.gregory(this.returnDate).toISOString()
                    : undefined,
            departureDateFa: this.departureDate
                ? dayConfig.jalali(this.departureDate).format('dddd YYYY/MM/DD')
                : undefined,
            returnDateFa:
                this.returnDate && this.tripType === TripType.RoundTrip
                    ? dayConfig
                          .jalali(this.returnDate)
                          .format('dddd MM/DD/YYYY')
                    : undefined,
            adultCount: `${this.adultCount}`,
            link: `https://mrbilit.com${this.viewUrl}`,
        };
    }

    // final
    override async loadPrices(payload?: { until: Date }) {
        try {
            if (!this.origin || !this.destination) {
                return;
            }

            const newLoader = this.getPriceLoader(
                this.origin,
                this.destination,
                this.tripType
            );

            const oldLoader = this.priceLoader;
            if (!oldLoader?.equals(newLoader)) {
                this.priceLoader = newLoader;
            } else if (oldLoader.tripType !== newLoader.tripType) {
                oldLoader?.switchTripType(this.tripType);
            }

            await this.priceLoader?.fetchPrices(payload?.until);
        } catch (e) {
            reportException(e);
        }
    }

    // final
    override get itinerarySearchDates() {
        return {
            start: this.departureDate,
            end: this.returnDate,
        };
    }

    // final
    addRecentLocation(item: TLocation) {
        const recentLocations = this.isDomestic
            ? this.domesticRecentLocations
            : this.foreignRecentLocations;
        // Removing existing
        const existingIndex = recentLocations.findIndex(
            (l: TLocation) => l.code === item.code
        );
        if (existingIndex > -1) {
            recentLocations.splice(existingIndex, 1);
        }

        // Adding new item
        recentLocations.unshift(item);

        // Checking for the limit
        if (recentLocations.length > RECENT_ITEMS_LIMIT) {
            recentLocations.pop();
        }
    }

    // final
    override saveSearch() {
        if (this.origin) {
            this.addRecentLocation(this.origin);
        }
        if (this.destination) {
            this.addRecentLocation(this.destination);
        }
        const data = {
            searchMode: this.searchMode,
            tripType: this.tripType,
            domesticOrigin: this.domesticOrigin,
            domesticDestination: this.domesticDestination,
            foreignOrigin: this.foreignOrigin,
            foreignDestination: this.foreignDestination,
            departureDate: this.departureDate,
            returnDate: this.returnDate,
            adultCount: this.adultCount,
            childCount: this.childCount,
            infantCount: this.infantCount,
            domesticRecentLocations: this.domesticRecentLocations,
            foreignRecentLocations: this.foreignRecentLocations,
        };
        const lastSearchParamsStr = localStorage.getItem(this.lastSearchKey);
        if (lastSearchParamsStr) {
            const lastSearchParams = JSON.parse(lastSearchParamsStr);
            Object.keys(data).forEach(key => {
                if ((data as any)[key] === undefined && lastSearchParams[key]) {
                    (data as any)[key] = lastSearchParams[key];
                }
            });
        }
        localStorage.setItem(this.lastSearchKey, JSON.stringify(data));
    }

    override async searchLocations(
        searchKeyword: string,
        options: { target: 'origin' | 'destination'; mode: SearchMode } = {
            mode: SearchMode.Domestic,
            target: 'origin',
        }
    ): Promise<void> {
        this.originExtraLocations = [];
        this.destinationExtraLocations = [];
        if (options.target === 'origin') {
            this.originSearchKeyword[options.mode] = searchKeyword;
            if (!this.filteredOrigins[0].locations.length) {
                await this.advancedSearch({
                    token: searchKeyword,
                    target: options.target,
                });
            }
        } else {
            this.destinationSearchKeyword[options.mode] = searchKeyword;
            if (!this.filteredDestinations[0].locations.length) {
                await this.advancedSearch({
                    token: searchKeyword,
                    target: options.target,
                });
            }
        }
    }

    // final
    override async advancedSearch({
        token,
        target,
    }: {
        token: string;
        target: 'origin' | 'destination';
    }) {
        if (this.isLoadingLocations) {
            return;
        }
        try {
            this.isLoadingLocations = true;
            const { data } = await axios.get<{
                recommendation: {
                    results?: { title: string; id: string }[];
                };
            }>(
                `https://data.mrbilit.ir/recommend/${this.reserveInfo.type}/${token}`
            );

            this[`${target}ExtraLocations` as const] = (
                (data.recommendation?.results || []) as []
            ).map<TLocation>(
                (loc: any) =>
                    ({
                        title: loc.title,
                        code: loc.id,
                        icon: faLocationDot,
                    }) as TLocation
            );
        } catch (e) {
            reportException(e);
        } finally {
            this.isLoadingLocations = false;
        }
    }

    // final
    override setDestination(destination: TLocation | null) {
        if (destination == null) {
            return;
        }
        switch (this.searchMode) {
            case SearchMode.Domestic:
                this.domesticDestination = destination;
                break;
            case SearchMode.Foreign:
                this.foreignDestination = destination;
                break;
        }
    }

    // final
    override setItinerarySearchDates(val: DateSpanObject | null) {
        if (this.tripType === TripType.RoundTrip && val) {
            const valSpan = val as DateSpanObject;
            this.departureDate =
                valSpan.start || this.departureDate || new Date();
            this.returnDate = valSpan.end;
        } else {
            const valDate = val?.start;
            this.departureDate = valDate || this.departureDate;
        }
    }

    // final
    override getSearchUrl(): Promise<string> {
        const analyticsStore = useAnalyticsStore();
        const searchFormStore = useSearchFormStore();
        if (process.client) {
            analyticsStore.trackAll(
                'search',
                analyticsStore.searchResultsAttributes
            );
        }
        this.saveSearch();
        searchFormStore.firstReserveType = this.reserveInfo.type;
        return Promise.resolve(this.viewUrl);
    }

    // final
    setSpecificOrigin({
        location,
        mode,
    }: {
        location: TLocation | null;
        mode: SearchMode;
    }) {
        switch (mode) {
            case SearchMode.Domestic:
                this.domesticOrigin = location;
                break;
            case SearchMode.Foreign:
                this.foreignOrigin = location;
                break;
        }
    }

    // final
    setSpecificDestination({
        location,
        mode,
    }: {
        location: TLocation | null;
        mode: SearchMode;
    }) {
        switch (mode) {
            case SearchMode.Domestic:
                this.domesticDestination = location;
                break;
            case SearchMode.Foreign:
                this.foreignDestination = location;
                break;
        }
    }

    // final
    get tripTypeOptions() {
        return [
            {
                label: 'یک‌طرفه',
                value: TripType.OneWay,
            },
            {
                label: 'رفت و برگشت',
                value: TripType.RoundTrip,
            },
        ];
    }

    // final
    swap() {
        switch (this.searchMode) {
            case SearchMode.Domestic:
                [this.domesticOrigin, this.domesticDestination] = [
                    this.domesticDestination,
                    this.domesticOrigin,
                ];
                break;
            case SearchMode.Foreign:
                [this.foreignOrigin, this.foreignDestination] = [
                    this.foreignDestination,
                    this.foreignOrigin,
                ];
                break;
        }
        const temp = this.destinationSearchKeyword[this.searchMode];
        this.destinationSearchKeyword[this.searchMode] =
            this.originSearchKeyword[this.searchMode];
        this.originSearchKeyword[this.searchMode] = temp;
    }

    // final
    override loadLastSearch(setSearchMode: boolean) {
        if (this.isLastSearchLoaded) {
            return;
        }
        const tomorrow = dayConfig
            .gregory()
            .add(1, 'day')
            .set('hours', 0)
            .set('minutes', 0)
            .set('seconds', 0)
            .toDate();
        const lastSearchParamsStr = localStorage.getItem(this.lastSearchKey);

        if (!lastSearchParamsStr) {
            this.departureDate = tomorrow;
            return;
        } else {
            const lastSearchParams = JSON.parse(lastSearchParamsStr);
            this.domesticRecentLocations =
                lastSearchParams.domesticRecentLocations;
            this.foreignRecentLocations =
                lastSearchParams.foreignRecentLocations;
            lastSearchParams.departureDate = dayConfig
                .gregory(
                    lastSearchParams.departureDate.date ||
                        lastSearchParams.departureDate
                )
                .toDate();
            if (lastSearchParams.returnDate) {
                lastSearchParams.returnDate = dayConfig
                    .gregory(
                        lastSearchParams.returnDate.date ||
                            lastSearchParams.returnDate
                    )
                    .toDate();
            }
            if (setSearchMode) {
                this.searchMode = lastSearchParams.searchMode;
            }
            this.setTripTypeAndCheckDate(lastSearchParams.tripType);
            this.setSpecificOrigin({
                location: lastSearchParams.domesticOrigin,
                mode: SearchMode.Domestic,
            });
            this.setSpecificDestination({
                location: lastSearchParams.domesticDestination,
                mode: SearchMode.Domestic,
            });
            this.setSpecificOrigin({
                location: lastSearchParams.foreignOrigin,
                mode: SearchMode.Foreign,
            });
            this.setSpecificDestination({
                location: lastSearchParams.foreignDestination,
                mode: SearchMode.Foreign,
            });
            this.originSearchKeyword = {
                [SearchMode.Foreign]: '',
                [SearchMode.Domestic]: '',
            };
            this.destinationSearchKeyword = {
                [SearchMode.Foreign]: '',
                [SearchMode.Domestic]: '',
            };
            if (
                lastSearchParams.departureDate &&
                dayConfig.gregory().isAfter(lastSearchParams.departureDate)
            ) {
                lastSearchParams.departureDate = tomorrow;
            }
            this.departureDate = lastSearchParams.departureDate;

            if (
                lastSearchParams.returnDate &&
                dayConfig.gregory().isAfter(lastSearchParams.returnDate)
            ) {
                lastSearchParams.returnDate = tomorrow;
            }
            this.returnDate = lastSearchParams.returnDate;

            if (
                lastSearchParams.adultCount === 0 ||
                lastSearchParams.adultCount
            ) {
                this.setPaxTypeCount({
                    type: PaxType.Adult,
                    count: lastSearchParams.adultCount,
                });
            }

            if (
                lastSearchParams.childCount === 0 ||
                lastSearchParams.childCount
            ) {
                this.setPaxTypeCount({
                    type: PaxType.Child,
                    count: lastSearchParams.childCount,
                });
            }

            if (
                lastSearchParams.infantCount === 0 ||
                lastSearchParams.infantCount
            ) {
                this.setPaxTypeCount({
                    type: PaxType.Infant,
                    count: lastSearchParams.infantCount,
                });
            }
            this.loadPrices();
        }
        this.isLastSearchLoaded = true;
    }

    async loadLandingSearchParams(destinationCode: string) {
        const origin = destinationCode === 'tehran' ? 'esfahan' : 'tehran';
        await this.searchLocations(origin);
        this.setOriginBaseOnCode(origin);
        await this.searchLocations(destinationCode, {
            target: 'destination',
            mode: SearchMode.Domestic,
        });
        this.setDestinationBaseOnCode(destinationCode);
    }
}
